import React, { useState } from 'react';
import { CSVLink } from "react-csv";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Export = ({ results, currentDateTime, selectedColumns }) => {
  const [showExportOptions, setShowExportOptions] = useState(false);

  const formatDataForExport = () => {
    if (!results.length) return [];
    
    // If no columns are selected, use all columns
    const columnsToExport = selectedColumns.length > 0 ? selectedColumns : Object.keys(results[0]);
    
    return results.map(row => {
      const formattedRow = {};
      columnsToExport.forEach(column => {
        formattedRow[column] = row[column];
      });
      return formattedRow;
    });
  };

  const exportToPDF = () => {
    try {
      if (!results.length) {
        alert('No data to export');
        return;
      }

      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text('Query Results', 105, 15, null, null, 'center');
      
      // Add underline
      doc.setLineWidth(0.5);
      doc.line(14, 17, 200, 17);
      
      // Add timestamp
      doc.setFontSize(10);
      doc.text(`Generated on: ${currentDateTime}`, 14, 25);

      // Get columns from selected columns or all columns
      const columnsToExport = selectedColumns.length > 0 ? selectedColumns : Object.keys(results[0]);
      
      // Prepare the data for autoTable
      const rows = results.map(result => columnsToExport.map(col => result[col]));

      // Add the table
      doc.autoTable({
        head: [columnsToExport.map(col => col.replace(/([A-Z])/g, " $1").trim())],
        body: rows,
        startY: 30,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [40, 84, 147] },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          // Add more column styles if needed
        },
        margin: { left: 10, right: 10 },
        pageBreak: 'auto',
        tableWidth: 'auto',
        showHead: 'everyPage',
        theme: 'grid',
        didDrawPage: function(data) {
          // Header
          doc.setFontSize(10);
          doc.setTextColor(150);
          doc.text('Page ' + doc.internal.getNumberOfPages(), data.settings.margin.left, 10);
        }
      });

      // Add footer with decreased opacity
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text('© Indent Management Confident', 105, doc.internal.pageSize.height - 10, null, null, 'center');
      }

      // Save the PDF
      const fileName = `query_results_${currentDateTime.replace(/[: ]/g, '_')}.pdf`;
      doc.save(fileName);
      setShowExportOptions(false);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF file. Please try again.');
    }
  };

  const exportToExcel = () => {
    try {
      if (!results.length) {
        alert('No data to export');
        return;
      }

      const formattedData = formatDataForExport();
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'QueryResults');
      
      const fileName = `query_results_${currentDateTime.replace(/[: ]/g, '_')}.xlsx`;
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, fileName);
      setShowExportOptions(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel file. Please try again.');
    }
  };

  return (
    <div className="relative inline-block text-left">
      <button
        onClick={() => setShowExportOptions(!showExportOptions)}
        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition flex items-center"
      >
        Export
        <svg 
          className={`ml-2 h-5 w-5 transition-transform ${showExportOptions ? 'rotate-180' : ''}`}
          fill="none" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth="2" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      {showExportOptions && (
        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1" role="menu" aria-orientation="vertical">
            <button
              onClick={exportToPDF}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
            >
              Export as PDF
            </button>
            <CSVLink
              data={formatDataForExport()}
              filename={`query_results_${currentDateTime.replace(/[: ]/g, '_')}.csv`}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
              onClick={() => setShowExportOptions(false)}
            >
              Export as CSV
            </CSVLink>
            <button
              onClick={exportToExcel}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
            >
              Export as Excel
            </button>
          </div>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {showExportOptions && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowExportOptions(false)}
        ></div>
      )}
    </div>
  );
};

export default Export;